import {createSelector} from '@reduxjs/toolkit';
import {RootState} from '../store';
import {THUNK_STATUS} from '../../domain/types';
import {TranscriptionSessionSnapshot} from '../slices/usageThunks';

/**
 * The single owner of "how much transcription time is left".
 *
 * Everything user-facing — the header pill, the transcribe confirmation, the "this file is longer
 * than your remaining time" warning — reads the balance from here, so they can never disagree about
 * the same number. The numbers themselves come straight from `GET /rest/v1/usage` (`usageThunks`)
 * and from the usage block the 402s and 429s carry; this file only combines them, it does not
 * derive a balance.
 *
 * **There are two meters and a job has to clear both.**
 *
 *   - The **period allowance** is a quota: so many seconds per billing cycle. Running out is a
 *     paywall — upgrade, or wait for the cycle to roll. The server answers 402.
 *   - The **session** is a rate throttle: a window that opens on the first transcription started
 *     while none is active, and carries its own audio budget. Spending it refuses further jobs
 *     until it resets. That is a *wait*, not a paywall — no plan shortens it. The server answers
 *     429 `session_limit_exceeded`.
 *
 * Having 20 hours left this period does not mean 20 hours can be started now, which is why
 * `startableSeconds` exists and is the number that gates the next action.
 */

const usageSelector = (state: RootState) => state.usage;

export const usageApiStatusSelector = (state: RootState) => usageSelector(state).api.status;
export const usageDataSelector = (state: RootState) => usageSelector(state).data;

export const usageIsLoadingSelector = createSelector(
  [usageApiStatusSelector],
  status => status === THUNK_STATUS.pending
);

/** How urgently a meter needs to be shown. Drives colour and the warning copy. */
export type QuotaLevel = 'ok' | 'low' | 'critical';

export interface TranscriptionQuota {
  secondsUsed: number;
  /** Null when the plan is uncapped. */
  secondsLimit: number | null;
  /** Null when the plan is uncapped. */
  secondsRemaining: number | null;
  /**
   * The longest single file the plan accepts, in seconds — null when uncapped.
   *
   * This is a *separate* ceiling from the balance and the two are routinely far apart: a lapsed
   * account can hold 15m of monthly time and still be refused a 6m file. Showing only the balance
   * is what made users read the 402 as a bug.
   */
  maxFileSeconds: number | null;
  /** ISO end of the current billing period — when the balance resets. Null until usage loads. */
  periodEnd: string | null;
  /**
   * The rate throttle, or null on a plan that has none.
   *
   * `session.isActive === false` means no session is open: the full budget is waiting and the
   * user's clock has not started. There is no deadline to render in that state.
   */
  session: TranscriptionSessionSnapshot | null;
  /**
   * The longest job that can be **started right now** — the lower of the two meters, null only when
   * neither caps anything.
   *
   * This is the number that decides whether the next action succeeds, and it is not the period
   * balance: 20 hours left this period with 40 minutes of session budget means 40 minutes.
   */
  startableSeconds: number | null;
  /** True when the session is what is holding `startableSeconds` down, rather than the period. */
  isSessionBound: boolean;
  /** The period meter. This is the one an upgrade can fix. */
  level: QuotaLevel;
  /** The session meter — always `ok` when the plan has no throttle. An upgrade cannot fix this. */
  sessionLevel: QuotaLevel;
  /** The worse of the two, for anything painting a single colour. */
  displayLevel: QuotaLevel;
  /** False until a usage read has landed — the pill renders nothing rather than a wrong `0m`. */
  isKnown: boolean;
}

/** Under 20% of the limit is "low"; nothing left at all is "critical". */
const LOW_FRACTION = 0.2;

/**
 * A minute of slack: a balance under 60s cannot transcribe anything worth having, so it reads as
 * empty rather than as a number the user might try to spend. An uncapped meter (null) never warns.
 */
function levelFor(remaining: number | null, limit: number | null): QuotaLevel {
  if (remaining === null) {
    return 'ok';
  }
  if (remaining < 60) {
    return 'critical';
  }
  return limit !== null && remaining < limit * LOW_FRACTION ? 'low' : 'ok';
}

const WORST: QuotaLevel[] = ['ok', 'low', 'critical'];

const worseOf = (a: QuotaLevel, b: QuotaLevel): QuotaLevel =>
  (WORST.indexOf(a) >= WORST.indexOf(b) ? a : b);

/**
 * What the session has available, which is not `secondsRemaining` when it is idle.
 *
 * An inactive session reports the full budget, but reading it that way relies on the server
 * zeroing `seconds_used` — and the contract's meaning of `active: false` is "the full budget is
 * waiting", so the limit is the honest answer and cannot drift.
 */
function sessionAvailable(session: TranscriptionSessionSnapshot | null): number | null {
  if (!session) {
    return null;
  }
  return session.isActive ? session.secondsRemaining : session.secondsLimit;
}

export const transcriptionQuotaSelector = createSelector(
  [usageDataSelector],
  (usage): TranscriptionQuota => {
    const secondsLimit = usage?.secondsLimit ?? null;
    const secondsRemaining = usage?.secondsRemaining ?? null;
    const session = usage?.session ?? null;

    const sessionRemaining = sessionAvailable(session);
    const level = levelFor(secondsRemaining, secondsLimit);
    // An idle session is a full one, so it never warns — the user is under no limit yet.
    const sessionLevel = session && session.isActive
      ? levelFor(session.secondsRemaining, session.secondsLimit)
      : 'ok';

    const candidates = [secondsRemaining, sessionRemaining].filter(
      (value): value is number => value !== null
    );
    const startableSeconds = candidates.length > 0 ? Math.min(...candidates) : null;

    return {
      secondsUsed: usage?.secondsUsed ?? 0,
      secondsLimit,
      secondsRemaining,
      maxFileSeconds: usage?.maxFileSeconds ?? null,
      periodEnd: usage?.periodEnd ?? null,
      session,
      startableSeconds,
      isSessionBound: sessionRemaining !== null
        && (secondsRemaining === null || sessionRemaining < secondsRemaining),
      level,
      sessionLevel,
      displayLevel: worseOf(level, sessionLevel),
      isKnown: usage != null,
    };
  }
);
